<script setup>
import { computed, reactive, ref, nextTick, watch } from 'vue'
import { trackQuoteSubmit, markQuoteSent } from '@/lib/analytics'
import { hashedUserData } from '@/lib/user_data'
import { requestedProductId, requestCount } from '@/lib/quote_intent'
import { validateForm, COUNTRY } from '@/lib/quoteValidation'
import WhatsappButton from '@/components/WhatsappButton.vue'

const props = defineProps({
  phone: { type: String, required: true }
})

const options = readOptions()

function readOptions () {
  const node = document.getElementById('quote-options')
  const empty = { products: [], communes: [] }

  try {
    return node ? { ...empty, ...JSON.parse(node.textContent) } : empty
  } catch {
    return empty
  }
}

function blankItem () {
  return {
    product_id: options.products[0]?.id ?? '',
    room: '',
    inside_width_cm: '',
    inside_height_cm: '',
    outside_width_cm: '',
    outside_height_cm: '',
    quantity: 1
  }
}

const form = reactive({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  street: '',
  unit: '',
  commune: '',
  comment: '',
  items: [ blankItem() ]
})

// Trampa para robots: invisible y fuera del orden de tabulación, así que solo
// la rellena algo que no está mirando la página.
const website = ref('')

const status = ref('idle')
const errors = reactive({ base: {}, items: [] })
const serverMessage = ref('')
const formEl = ref(null)
const successEl = ref(null)

const sending = computed(() => status.value === 'sending')
const sent = computed(() => status.value === 'sent')

const MAX_ITEMS = 20

// Las mismas listas que ya restringen los <select>, ahora también para validar:
// lo que se puede elegir y lo que se acepta siguen siendo la misma lista.
const productIds = options.products.map((product) => product.id)
const validationOptions = { communes: options.communes, productIds }

// Campos que el visitante ya tocó: solo esos muestran su error mientras llena,
// para no gritarle un "falta el correo" sobre un campo que todavía no visita.
// Al enviar (submitted) se muestran todos.
const touched = reactive(new Set())
const submitted = ref(false)
const clientErrors = computed(() => validateForm(form, validationOptions))

function markTouched (name) {
  touched.add(name)
}

function markItemTouched (index, name) {
  touched.add(`items.${index}.${name}`)
}

// Al agregar o quitar una ventana los índices se corren; se olvida qué campo de
// ventana estaba tocado para no mostrar un error en la fila equivocada.
function clearItemTouched () {
  for (const key of [ ...touched ]) {
    if (key.startsWith('items.')) touched.delete(key)
  }
}

function addItem () {
  if (form.items.length >= MAX_ITEMS) return

  form.items.push(blankItem())
  errors.items.push({})
  clearItemTouched()
}

function removeItem (index) {
  if (form.items.length === 1) return

  form.items.splice(index, 1)
  errors.items.splice(index, 1)
  clearItemTouched()
}

function dedupe (messages) {
  return [ ...new Set(messages) ]
}

// Une el error del cliente (solo si el campo ya se tocó o ya se intentó enviar)
// con el que haya devuelto el servidor. En la práctica no se pisan: si el
// cliente atajó el error, no llegamos a enviar.
function fieldErrors (name) {
  const server = errors.base[name] || []
  const show = submitted.value || touched.has(name)
  const client = show ? (clientErrors.value.base[name] || []) : []
  return dedupe([ ...client, ...server ])
}

function itemErrors (index, name) {
  const server = errors.items[index]?.[name] || []
  const show = submitted.value || touched.has(`items.${index}.${name}`)
  const client = show ? (clientErrors.value.items[index]?.[name] || []) : []
  return dedupe([ ...client, ...server ])
}

function clearErrors () {
  Object.keys(errors.base).forEach((key) => delete errors.base[key])
  errors.items = form.items.map(() => ({}))
  serverMessage.value = ''
}

const csrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.content ?? ''

async function submit () {
  if (sending.value) return

  submitted.value = true
  clearErrors()

  // El servidor sigue teniendo la última palabra, pero si el cliente ya ve
  // errores no lo molestamos: los mostramos todos, llevamos el foco al primero
  // y no enviamos.
  if (!clientErrors.value.valid) {
    serverMessage.value = 'Revisa los campos marcados en rojo.'
    await nextTick()
    formEl.value
      ?.querySelector('[aria-invalid="true"]')
      ?.focus({ preventScroll: false })
    return
  }

  status.value = 'sending'

  try {
    const response = await fetch('/cotizaciones', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken()
      },
      body: JSON.stringify({
        website: website.value,
        quote: { ...form, items_attributes: form.items, items: undefined }
      })
    })

    const payload = await response.json().catch(() => ({}))

    if (response.ok) {
      serverMessage.value = payload.message ?? ''
      status.value = 'sent'

      // Hashear es asíncrono, así que se espera antes de avisar: el evento tiene
      // que salir con el dato adentro. Si el navegador no puede hashear vuelve
      // null y la conversión se manda igual, solo que sin emparejar.
      const userData = await hashedUserData({ email: form.email, phone: form.phone })

      trackQuoteSubmit({
        items: form.items.length,
        blinds: form.items.reduce((total, item) => total + Number(item.quantity || 0), 0),
        commune: form.commune,
        userData
      })

      // La URL cambia después del evento, no antes: así el dato de emparejado ya
      // está en el dataLayer cuando Tag Manager ve el cambio de historial, y da
      // lo mismo cuál de los dos disparadores use quien configure la conversión.
      markQuoteSent()

      // La tarjeta de éxito es mucho más corta que el formulario, así que al
      // reemplazarlo la página se acorta y el scroll —que estaba en el botón—
      // queda en la sección de abajo. Hay que llevar el ojo a la confirmación.
      await nextTick()
      successEl.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })

      return
    }

    if (payload.errors) {
      const { items = [], ...base } = payload.errors
      Object.assign(errors.base, base)
      errors.items = items
    }

    serverMessage.value = payload.message ?? ''
    status.value = 'idle'

    // Sin esto el foco se queda abajo, en el botón, y el error queda arriba
    // fuera de pantalla.
    await nextTick()
    formEl.value
      ?.querySelector('[aria-invalid="true"]')
      ?.focus({ preventScroll: false })
  } catch {
    serverMessage.value =
      'No pudimos enviar el formulario. Revisa tu conexión o escríbenos por WhatsApp.'
    status.value = 'idle'
  }
}

// Volver al formulario en blanco para una cotización nueva. Es una acción
// explícita desde la tarjeta de éxito —no el formulario que se queda solo—, así
// que no reenvía por accidente: hay que llenar y enviar de nuevo a propósito.
async function sendAnother () {
  Object.assign(form, {
    first_name: '', last_name: '', email: '', phone: '',
    street: '', unit: '', commune: '', comment: ''
  })
  form.items.splice(0, form.items.length, blankItem())
  touched.clear()
  submitted.value = false
  clearErrors()
  status.value = 'idle'

  await nextTick()
  document.getElementById('cotizar')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

// Alguien apretó "Cotizar por el formulario" en la ficha de un producto: ya
// eligió qué quiere, así que llega elegido. Se mira el contador y no el id,
// para que pedir dos veces el mismo producto cuente como dos pedidos.
watch(requestCount, () => applyRequestedProduct(requestedProductId.value))

async function applyRequestedProduct (id) {
  if (!id || !productIds.includes(id)) return

  // Si ya se envió una cotización, la pantalla es la de confirmación: se repone
  // el formulario en blanco antes de escribir nada en él.
  if (sent.value) await sendAnother()

  // La primera ventana sin medidas es la que recibe el producto. Si todas están
  // escritas, se agrega otra en vez de pisar lo que la persona ya llenó.
  let index = form.items.findIndex(isBlankItem)

  if (index === -1) {
    if (form.items.length >= MAX_ITEMS) return

    addItem()
    index = form.items.length - 1
  }

  form.items[index].product_id = id

  await nextTick()
  document.getElementById(`q-product-${index}`)?.focus({ preventScroll: true })
}

function isBlankItem (item) {
  return !item.room &&
    !item.inside_width_cm && !item.inside_height_cm &&
    !item.outside_width_cm && !item.outside_height_cm
}

const field =
  'w-full rounded-md border border-linen/15 bg-tobacco px-4 py-3 text-linen transition-colors placeholder:text-linen/45 focus:border-glow focus:outline-none focus:ring-2 focus:ring-glow/30'
const fieldBad = 'border-red-400'
const label = 'block text-sm font-semibold text-linen'
const hint = 'mt-1 text-sm text-linen/65'
// red-400 y no red-700: sobre fondo oscuro, el rojo de siempre se apaga y no
// llega a 4.5:1.
const errorText = 'mt-1 text-sm font-medium text-red-400'
</script>

<template>
  <!-- Enviado: el formulario se reemplaza por la confirmación. No se queda en
       pantalla porque invitaría a reenviarlo sin querer (dos correos para el
       mismo contacto); para mandar otra hay un botón explícito que lo repone en
       blanco. -->
  <div
    v-if="sent"
    ref="successEl"
    class="scroll-mt-24 rounded-lg bg-night p-8 text-center text-linen ring-1 ring-linen/10 lg:p-12"
    role="status"
  >
    <p class="eyebrow text-glow">Listo</p>
    <h3 class="display mt-4 text-balance text-[clamp(1.6rem,3.5vw,2.4rem)]">
      Recibimos tus medidas
    </h3>
    <p class="mx-auto mt-4 max-w-md leading-relaxed text-linen/65">
      {{ serverMessage }} También te llegó una copia a
      <strong class="font-semibold text-linen">{{ form.email }}</strong>.
    </p>

    <div class="mt-8 flex flex-col items-center gap-4">
      <div class="flex flex-wrap items-center justify-center gap-3">
        <WhatsappButton
          :phone="phone"
          variant="glow"
          subject="preguntar por una cotización que ya envié por el formulario"
          label="Escribir por WhatsApp"
          source="quote-form-success"
        />
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-full border border-linen/30 px-6 py-3.5 font-semibold text-linen transition-colors hover:border-linen hover:bg-linen hover:text-night"
          @click="sendAnother"
        >
          Enviar otra cotización
        </button>
      </div>
      <p class="text-sm text-linen/65">¿Se te quedó una ventana? Escríbenos o parte una nueva.</p>
    </div>
  </div>

  <form
    v-else
    ref="formEl"
    class="rounded-lg bg-night p-6 text-linen ring-1 ring-linen/10 scheme-dark sm:p-8 lg:p-10"
    novalidate
    @submit.prevent="submit"
  >
    <!-- Honeypot -->
    <div class="pointer-events-none absolute left-[-9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
      <label>
        No llenes esto
        <input v-model="website" type="text" name="website" tabindex="-1" autocomplete="off" />
      </label>
    </div>

    <fieldset>
      <legend class="eyebrow text-glow">Tus datos</legend>

      <div class="mt-5 grid gap-x-6 gap-y-5 sm:grid-cols-2">
        <div>
          <label class="block" for="q-first-name"><span :class="label">Nombre</span></label>
          <input
            id="q-first-name"
            v-model="form.first_name"
            :class="[ field, fieldErrors('first_name').length && fieldBad, 'mt-1.5' ]"
            :aria-invalid="fieldErrors('first_name').length > 0"
            type="text"
            autocomplete="given-name"
            @blur="markTouched('first_name')"
          />
          <p v-for="message in fieldErrors('first_name')" :key="message" :class="errorText">{{ message }}</p>
        </div>

        <div>
          <label class="block" for="q-last-name"><span :class="label">Apellido</span></label>
          <input
            id="q-last-name"
            v-model="form.last_name"
            :class="[ field, fieldErrors('last_name').length && fieldBad, 'mt-1.5' ]"
            :aria-invalid="fieldErrors('last_name').length > 0"
            type="text"
            autocomplete="family-name"
            @blur="markTouched('last_name')"
          />
          <p v-for="message in fieldErrors('last_name')" :key="message" :class="errorText">{{ message }}</p>
        </div>

        <div>
          <label class="block" for="q-email"><span :class="label">Correo</span></label>
          <input
            id="q-email"
            v-model="form.email"
            :class="[ field, fieldErrors('email').length && fieldBad, 'mt-1.5' ]"
            :aria-invalid="fieldErrors('email').length > 0"
            type="email"
            inputmode="email"
            autocomplete="email"
            placeholder="tunombre@correo.cl"
            @blur="markTouched('email')"
          />
          <p v-for="message in fieldErrors('email')" :key="message" :class="errorText">{{ message }}</p>
        </div>

        <div>
          <label class="block" for="q-phone"><span :class="label">Celular</span></label>
          <!-- El código de país va fijo en Chile: el backend solo acepta
               celulares válidos, así que un selector de países le mentiría a
               quien lo cambie. Se muestra para que quede claro qué se espera. -->
          <div
            :class="[ 'mt-1.5 flex items-stretch overflow-hidden rounded-md border bg-tobacco transition-colors focus-within:border-glow focus-within:ring-2 focus-within:ring-glow/30', fieldErrors('phone').length ? 'border-red-400' : 'border-linen/15' ]"
          >
            <span
              class="flex select-none items-center gap-1.5 border-r border-linen/15 px-3 text-linen/80"
              :title="`Código de país: ${COUNTRY.name}`"
            >
              <span aria-hidden="true">{{ COUNTRY.flag }}</span>
              <span>{{ COUNTRY.code }}</span>
            </span>
            <input
              id="q-phone"
              v-model="form.phone"
              class="w-full bg-transparent px-4 py-3 text-linen placeholder:text-linen/45 focus:outline-none"
              :aria-invalid="fieldErrors('phone').length > 0"
              aria-describedby="q-phone-hint"
              type="tel"
              inputmode="tel"
              autocomplete="tel"
              placeholder="9 1234 5678"
              @blur="markTouched('phone')"
            />
          </div>
          <p v-for="message in fieldErrors('phone')" :key="message" :class="errorText">{{ message }}</p>
        </div>
      </div>
    </fieldset>

    <fieldset class="mt-10">
      <legend class="eyebrow text-glow">Dónde instalamos</legend>
      <p :class="[ hint, 'mt-2' ]">Por ahora solo llegamos a la Región Metropolitana.</p>

      <div class="mt-5 grid gap-x-6 gap-y-5 sm:grid-cols-2">
        <div class="sm:col-span-2">
          <label class="block" for="q-street"><span :class="label">Calle y número</span></label>
          <input
            id="q-street"
            v-model="form.street"
            :class="[ field, fieldErrors('street').length && fieldBad, 'mt-1.5' ]"
            :aria-invalid="fieldErrors('street').length > 0"
            type="text"
            autocomplete="street-address"
            placeholder="Av. Los Leones 1234"
            @blur="markTouched('street')"
          />
          <p v-for="message in fieldErrors('street')" :key="message" :class="errorText">{{ message }}</p>
        </div>

        <div>
          <label class="block" for="q-unit">
            <span :class="label">Depto o casa <span class="font-normal text-linen/65">(opcional)</span></span>
          </label>
          <input
            id="q-unit"
            v-model="form.unit"
            :class="[ field, fieldErrors('unit').length && fieldBad, 'mt-1.5' ]"
            :aria-invalid="fieldErrors('unit').length > 0"
            type="text"
            placeholder="Depto 802 · Casa 14"
            @blur="markTouched('unit')"
          />
          <p v-for="message in fieldErrors('unit')" :key="message" :class="errorText">{{ message }}</p>
        </div>

        <div>
          <label class="block" for="q-commune"><span :class="label">Comuna</span></label>
          <select
            id="q-commune"
            v-model="form.commune"
            :class="[ field, fieldErrors('commune').length && fieldBad, 'mt-1.5' ]"
            :aria-invalid="fieldErrors('commune').length > 0"
            @change="markTouched('commune')"
            @blur="markTouched('commune')"
          >
            <option value="">Elige tu comuna</option>
            <option v-for="commune in options.communes" :key="commune" :value="commune">
              {{ commune }}
            </option>
          </select>
          <p v-for="message in fieldErrors('commune')" :key="message" :class="errorText">{{ message }}</p>
        </div>
      </div>
    </fieldset>

    <fieldset class="mt-10">
      <legend class="eyebrow text-glow">Tus ventanas</legend>
      <p :class="[ hint, 'mt-2' ]">
        Una por ventana, con las dos medidas de la guía de acá arriba.
      </p>

      <ol class="mt-5 space-y-5">
        <li
          v-for="(item, index) in form.items"
          :key="index"
          class="rounded-md bg-cocoa p-5 ring-1 ring-linen/10"
        >
          <div class="flex items-baseline justify-between gap-4">
            <p class="eyebrow text-linen/65">Ventana {{ index + 1 }}</p>
            <button
              v-if="form.items.length > 1"
              type="button"
              class="-my-2 py-2 text-sm font-semibold text-linen/65 underline decoration-linen/25 underline-offset-4 transition-colors hover:text-red-400"
              @click="removeItem(index)"
            >
              Quitar
            </button>
          </div>

          <div class="mt-4 grid gap-x-5 gap-y-4 sm:grid-cols-2">
            <div>
              <label class="block" :for="`q-product-${index}`"><span :class="label">Cortina</span></label>
              <select
                :id="`q-product-${index}`"
                v-model="item.product_id"
                :class="[ field, itemErrors(index, 'product_id').length && fieldBad, 'mt-1.5' ]"
                :aria-invalid="itemErrors(index, 'product_id').length > 0"
                @change="markItemTouched(index, 'product_id')"
                @blur="markItemTouched(index, 'product_id')"
              >
                <option v-for="product in options.products" :key="product.id" :value="product.id">
                  {{ product.name }}
                </option>
              </select>
              <p v-for="message in itemErrors(index, 'product_id')" :key="message" :class="errorText">{{ message }}</p>
            </div>

            <div>
              <label class="block" :for="`q-room-${index}`">
                <span :class="label">Ambiente <span class="font-normal text-linen/65">(opcional)</span></span>
              </label>
              <input
                :id="`q-room-${index}`"
                v-model="item.room"
                :class="[ field, itemErrors(index, 'room').length && fieldBad, 'mt-1.5' ]"
                type="text"
                placeholder="Dormitorio principal"
              />
              <p v-for="message in itemErrors(index, 'room')" :key="message" :class="errorText">{{ message }}</p>
            </div>

            <!-- Las dos medidas van juntas y las dos son obligatorias: con el
                 vano y el marco al frente, el montaje lo decide Casa Lumina y
                 nadie tiene que volver a preguntar. -->
            <div class="sm:col-span-2">
              <p :class="label">Dentro del vano</p>

              <div class="mt-2 grid grid-cols-2 gap-x-4">
                <div>
                  <label class="block" :for="`q-inside-width-${index}`">
                    <span class="text-sm text-linen/65">Ancho (cm)</span>
                  </label>
                  <input
                    :id="`q-inside-width-${index}`"
                    v-model="item.inside_width_cm"
                    :class="[ field, itemErrors(index, 'inside_width_cm').length && fieldBad, 'mt-1' ]"
                    :aria-invalid="itemErrors(index, 'inside_width_cm').length > 0"
                    type="number"
                    inputmode="numeric"
                    min="20"
                    max="600"
                    @blur="markItemTouched(index, 'inside_width_cm')"
                  />
                  <p v-for="message in itemErrors(index, 'inside_width_cm')" :key="message" :class="errorText">{{ message }}</p>
                </div>

                <div>
                  <label class="block" :for="`q-inside-height-${index}`">
                    <span class="text-sm text-linen/65">Alto (cm)</span>
                  </label>
                  <input
                    :id="`q-inside-height-${index}`"
                    v-model="item.inside_height_cm"
                    :class="[ field, itemErrors(index, 'inside_height_cm').length && fieldBad, 'mt-1' ]"
                    :aria-invalid="itemErrors(index, 'inside_height_cm').length > 0"
                    type="number"
                    inputmode="numeric"
                    min="20"
                    max="600"
                    @blur="markItemTouched(index, 'inside_height_cm')"
                  />
                  <p v-for="message in itemErrors(index, 'inside_height_cm')" :key="message" :class="errorText">{{ message }}</p>
                </div>
              </div>
            </div>

            <div class="sm:col-span-2">
              <p :class="label">Fuera del vano</p>

              <div class="mt-2 grid grid-cols-2 gap-x-4">
                <div>
                  <label class="block" :for="`q-outside-width-${index}`">
                    <span class="text-sm text-linen/65">Ancho (cm)</span>
                  </label>
                  <input
                    :id="`q-outside-width-${index}`"
                    v-model="item.outside_width_cm"
                    :class="[ field, itemErrors(index, 'outside_width_cm').length && fieldBad, 'mt-1' ]"
                    :aria-invalid="itemErrors(index, 'outside_width_cm').length > 0"
                    type="number"
                    inputmode="numeric"
                    min="20"
                    max="600"
                    @blur="markItemTouched(index, 'outside_width_cm')"
                  />
                  <p v-for="message in itemErrors(index, 'outside_width_cm')" :key="message" :class="errorText">{{ message }}</p>
                </div>

                <div>
                  <label class="block" :for="`q-outside-height-${index}`">
                    <span class="text-sm text-linen/65">Alto (cm)</span>
                  </label>
                  <input
                    :id="`q-outside-height-${index}`"
                    v-model="item.outside_height_cm"
                    :class="[ field, itemErrors(index, 'outside_height_cm').length && fieldBad, 'mt-1' ]"
                    :aria-invalid="itemErrors(index, 'outside_height_cm').length > 0"
                    type="number"
                    inputmode="numeric"
                    min="20"
                    max="600"
                    @blur="markItemTouched(index, 'outside_height_cm')"
                  />
                  <p v-for="message in itemErrors(index, 'outside_height_cm')" :key="message" :class="errorText">{{ message }}</p>
                </div>
              </div>
            </div>

            <div>
              <label class="block" :for="`q-quantity-${index}`">
                <span :class="label">Cantidad</span>
              </label>
              <input
                :id="`q-quantity-${index}`"
                v-model="item.quantity"
                :class="[ field, itemErrors(index, 'quantity').length && fieldBad, 'mt-1.5' ]"
                :aria-invalid="itemErrors(index, 'quantity').length > 0"
                type="number"
                inputmode="numeric"
                min="1"
                max="30"
                @blur="markItemTouched(index, 'quantity')"
              />
              <p class="mt-1 text-sm text-linen/65">Ventanas iguales, con la misma medida.</p>
              <p v-for="message in itemErrors(index, 'quantity')" :key="message" :class="errorText">{{ message }}</p>
            </div>
          </div>
        </li>
      </ol>

      <button
        v-if="form.items.length < MAX_ITEMS"
        type="button"
        class="mt-5 inline-flex items-center gap-2 rounded-full border border-linen/30 px-5 py-3 font-semibold text-linen transition-colors hover:border-linen hover:bg-linen hover:text-night"
        @click="addItem"
      >
        <span aria-hidden="true">+</span>
        Agregar otra ventana
      </button>
    </fieldset>

    <fieldset class="mt-10">
      <legend class="eyebrow text-glow">Algo más</legend>

      <label class="mt-5 block" for="q-comment">
        <span :class="label">Comentario <span class="font-normal text-linen/65">(opcional)</span></span>
      </label>
      <textarea
        id="q-comment"
        v-model="form.comment"
        :class="[ field, fieldErrors('comment').length && fieldBad, 'mt-1.5' ]"
        rows="3"
        placeholder="La ventana del living da al poniente, pega mucho sol en la tarde."
        @blur="markTouched('comment')"
      ></textarea>
      <p v-for="message in fieldErrors('comment')" :key="message" :class="errorText">{{ message }}</p>
    </fieldset>

    <p
      v-if="serverMessage"
      class="mt-8 rounded-md bg-red-950 px-4 py-3 font-medium text-red-200 ring-1 ring-red-400/30"
      role="alert"
    >
      {{ serverMessage }}
    </p>

    <div class="mt-8 flex flex-wrap items-center gap-x-6 gap-y-4 border-t border-linen/12 pt-8">
      <button
        type="submit"
        class="inline-flex items-center gap-2 rounded-full bg-glow px-7 py-4 font-semibold text-night transition-colors hover:bg-[#f4a62a] disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="sending"
        :aria-busy="sending"
      >
        <svg
          v-if="sending"
          class="h-[1.15em] w-[1.15em] shrink-0 animate-spin"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" class="opacity-25" />
          <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
        </svg>
        {{ sending ? 'Enviando…' : 'Pedir cotización' }}
      </button>

      <!-- Bajo el botón y no en letra chica: acá es donde el cliente entrega el
           correo, el teléfono y la dirección de su casa. -->
      <p class="text-sm text-linen/60">
        Tus datos se usan solo para responder esta cotización.
        <a
          href="/privacidad"
          target="_blank"
          rel="noopener"
          class="text-linen/80 underline decoration-linen/25 underline-offset-4 transition-colors hover:text-glow hover:decoration-glow"
        >Política de privacidad</a>.
      </p>
    </div>
  </form>
</template>
